import i18n from '@/utils/i18n';
import React, { ReactNode } from 'react';
import { createStyles } from '@/contexts/theme-context';
import { Icons } from '@usepepper/sprout-kit-ui';
import { Image } from 'expo-image';
import { LayoutSize, useLayoutContext } from '@/contexts/layout-context';
import { Platform, Pressable, ScrollView, View } from 'react-native';
import {
  SIDEBAR_NAV_FADE_HEIGHT,
  SidebarNavScrollFade,
} from '@experiences/components/sidebar-nav-scroll-fade';
import { SwitchExperienceDrawer } from '@experiences/containers/switch-experience-drawer/switch-experience-drawer';

export const SIDEBAR_WIDTH = 232;

interface BaseSidebarProps {
  children: ReactNode;
  footer?: ReactNode;
  onToggleCollapse: () => void;
}

export const BaseSidebar: React.FC<BaseSidebarProps> = ({
  children,
  footer,
  onToggleCollapse,
}) => {
  const { layoutSize } = useLayoutContext();
  const isDrawerMode =
    layoutSize === LayoutSize.SMALL || layoutSize === LayoutSize.SMALL_TABLET;
  const [styles, theme] = useStyles();
  const logoUrl = theme.images.welcomeLogoUrl;

  return (
    <View
      style={[
        styles.container,
        isDrawerMode
          ? Platform.OS === 'web' && styles.containerDrawer
          : styles.containerInline,
      ]}
    >
      <View style={styles.content}>
        <View style={styles.logoContainer}>
          <Image
            alt={i18n.t('sales_portal.sidebar.logo')}
            contentFit="contain"
            source={{ uri: logoUrl }}
            style={styles.logo}
          />
          <Pressable
            accessibilityLabel={i18n.t('sales_portal.toggle_sidebar')}
            accessibilityRole="button"
            onPress={onToggleCollapse}
            style={styles.collapseButton}
          >
            {isDrawerMode ? (
              <Icons.Menu color={theme.sk.color.icon.secondary} size={16} />
            ) : (
              <Icons.PanelLeft
                color={theme.sk.color.icon.secondary}
                size={16}
              />
            )}
          </Pressable>
        </View>
        <View style={styles.navContainer}>
          <ScrollView
            contentContainerStyle={styles.navItems}
            showsVerticalScrollIndicator={false}
            style={styles.navScroll}
            testID="sidebar-nav-scroll"
          >
            {children}
          </ScrollView>
          <SidebarNavScrollFade />
        </View>
      </View>
      {footer}
      <SwitchExperienceDrawer />
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  collapseButton: {
    alignItems: 'center',
    height: 28,
    justifyContent: 'center',
    width: 28,
  },
  container: {
    flex: 1,
    paddingBottom: theme.sk.spacing.spaceMedium,
  },
  containerDrawer: {
    padding: theme.sk.spacing.spaceSmall,
  },
  containerInline: {
    backgroundColor: theme.sk.color.bg.primary.default,
    borderRightColor: theme.sk.color.bg.secondary.default,
    borderRightWidth: theme.sk.spacing.spaceSmall,
    padding: theme.sk.spacing.spaceSmall,
    width: SIDEBAR_WIDTH,
  },
  content: {
    flex: 1,
  },
  logo: {
    height: theme.sk.spacing.space2xlarge,
    width: 104,
  },
  logoContainer: {
    alignItems: 'flex-start',
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 10,
  },
  navContainer: {
    flex: 1,
    marginBottom: theme.sk.spacing.spaceSmall,
  },
  navItems: {
    alignItems: 'flex-start',
    paddingBottom: SIDEBAR_NAV_FADE_HEIGHT,
    paddingTop: theme.sk.spacing.spaceMedium,
  },
  navScroll: {
    flex: 1,
  },
}));
